/**
 * View Toggle Functionality for Product Collections
 *
 * Handles:
 * - Switching between grid and list view
 * - Saving user preferences in localStorage
 * - Updating product listings dynamically
 * - Quantity controls for list view
 * - Price updates when changing variants
 * - Support for filtering and pagination
 */

const LIST_VIEW_HIDDEN_VARIANTS = ['PALL', 'LAV', 'KARTWMS', 'HALVPALL', 'Default Title'];

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function safeParseJson(text, fallback) {
    try {
        return JSON.parse(text);
    } catch (error) {
        return fallback;
    }
}

function getCardElement(productElement) {
    return productElement?.querySelector('.card, .product-item, .product-card') || null;
}

function getVariantUnit(variant, unitsMap) {
    if (!variant || !unitsMap) return '';

    const optionValue = String(variant.option1 || '');
    const candidates = [optionValue, optionValue.toUpperCase(), optionValue.toLowerCase()];

    for (const candidate of candidates) {
        if (candidate && Object.prototype.hasOwnProperty.call(unitsMap, candidate)) {
            return String(unitsMap[candidate]);
        }
    }

    return '';
}

function formatListViewMoney(cents) {
    const value = Number(cents);
    if (Number.isNaN(value)) return '';

    const locale = document.documentElement.lang || 'sv-SE';
    const amount = value / 100;

    return `${new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount)} kr`;
}

function renderListViewCartIcon() {
    return `
      <svg class="list-view-action-icon" viewBox="0 0 512 512" aria-hidden="true" focusable="false">
        <path d="M48 80h64l48 272h256" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"></path>
        <path d="M160 288h249.44a8 8 0 0 0 7.85-6.43l28.8-144a8 8 0 0 0-7.85-9.57H128" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"></path>
        <circle cx="176" cy="416" r="16" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"></circle>
        <circle cx="400" cy="416" r="16" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"></circle>
      </svg>
    `;
}

function renderListViewSoldOutIcon() {
    return `
      <svg class="list-view-action-icon" viewBox="0 0 512 512" aria-hidden="true" focusable="false">
        <circle cx="256" cy="256" r="208" fill="none" stroke="currentColor" stroke-linecap="round" stroke-miterlimit="10" stroke-width="32"></circle>
        <path d="M108.92 108.92l294.16 294.16" fill="none" stroke="currentColor" stroke-linecap="round" stroke-miterlimit="10" stroke-width="32"></path>
      </svg>
    `;
}

function getListViewVariants(originalCard) {
    if (!originalCard) return [];

    if (originalCard.dataset.listVariantsParsed === 'true' && Array.isArray(originalCard._listVariants)) {
        return originalCard._listVariants;
    }

    const variantsScript = originalCard.querySelector('.js-card-product-variants');
    const unitsScript = originalCard.querySelector('.js-card-units-map');
    const variants = safeParseJson(variantsScript?.textContent || '[]', []);
    const unitsMap = safeParseJson(unitsScript?.textContent || '{}', {});
    const hideUnitOne = originalCard.dataset.hideUnitOne === 'true';
    const preferredLabel = (originalCard.dataset.preferredVariantLabel || '').trim();

    const hasOnlyDefaultVariant = variants.length === 1 && variants[0].title === 'Default Title';
    const visibleCandidates = variants.filter((variant) => hasOnlyDefaultVariant || !LIST_VIEW_HIDDEN_VARIANTS.includes(variant.title));
    const nonUnitOneCount = visibleCandidates.filter((variant) => getVariantUnit(variant, unitsMap) !== '1').length;

    const visibleVariants = visibleCandidates.filter((variant) => {
        const unit = getVariantUnit(variant, unitsMap);
        const isPreferred = preferredLabel !== '' && variant.available &&
            [variant.title?.trim(), variant.option1, variant.option2, variant.option3].includes(preferredLabel);
        if (hideUnitOne && unit === '1' && nonUnitOneCount > 0 && !isPreferred) {
            return false;
        }
        return true;
    });

    originalCard._listVariants = visibleVariants;
    originalCard._listUnitsMap = unitsMap;
    originalCard.dataset.listVariantsParsed = 'true';

    return visibleVariants;
}

function getSelectedListVariant(originalCard, variants) {
    if (!variants || variants.length === 0) return null;

    const selectedId = Number(originalCard?.dataset.visibleVariantId || 0);
    if (selectedId) {
        const selectedVariant = variants.find((variant) => Number(variant.id) === selectedId);
        if (selectedVariant) return selectedVariant;
    }

    return variants.find((variant) => variant.available) || variants[0] || null;
}

function getListViewVariantLabel(variant, unitsMap) {
    if (!variant) return '';

    const variantLabel = String(variant.option1 || variant.title || '');
    let unitLabel = '';

    if (unitsMap && variantLabel) {
        unitLabel = unitsMap[variantLabel];

        if (unitLabel == null || unitLabel === '') {
            unitLabel = unitsMap[variantLabel.toUpperCase()];
        }

        if (unitLabel == null || unitLabel === '') {
            unitLabel = unitsMap[variantLabel.toLowerCase()];
        }
    }

    if (unitLabel != null && unitLabel !== '') {
        return `${variantLabel} - ${unitLabel}`;
    }

    return variantLabel;
}

function renderListViewSelect(variants, selectedVariant, selectLabel) {
    const hasOnlyDefaultVariant = variants.length === 1 && variants[0].title === 'Default Title';
    return `
      <select class="list-view-select${hasOnlyDefaultVariant ? ' hidden' : ''}" aria-label="${selectLabel}">
        ${variants
          .map((variant) => {
              const selected = selectedVariant && Number(variant.id) === Number(selectedVariant.id) ? ' selected' : '';
              const disabled = variant.available === false ? ' disabled' : '';
              const optionLabel = variant.listViewTitle || variant.title;
              return `<option value="${variant.id}"${selected}${disabled}>${escapeHtml(optionLabel)}</option>`;
          })
          .join('')}
      </select>
    `;
}

function buildListViewMarkup(productElement, originalCard) {
    if (!productElement || !originalCard) return '';

    const titleLink = originalCard.querySelector('.card__heading a, .card__heading .full-unstyled-link, a[href*="/products/"]');
    const titleText = titleLink ? titleLink.textContent.trim() : '';
    const productUrl = originalCard.dataset.productUrl || titleLink?.href || '#';
    const productTitle = originalCard.dataset.productTitle || titleText;
    const productImage = originalCard.dataset.productImage || '';
    const productImageAlt = originalCard.dataset.productImageAlt || productTitle;
    const productMiljoLogosHtml = originalCard.querySelector('.miljo-markning-logos')?.outerHTML || '';
    const originalForm = originalCard.querySelector('form[action="/cart/add"]');
    const formId = originalForm ? originalForm.id : '';
    const canShowCommerce = originalCard.dataset.canShowCommerce === 'true';
    const isReplacedProduct = originalCard.dataset.isReplacedProduct === 'true';
    const isRetiredProduct = originalCard.dataset.isRetiredProduct === 'true';
    const replacementType = originalCard.dataset.listReplacementType || (isRetiredProduct ? 'retired' : 'replaced');
    const isTemporarilyOutOfStock = replacementType === 'temporary';
    const replacementProductUrl = originalCard.dataset.replacementProductUrl || '';
    const replacementProductTitle = originalCard.dataset.replacementProductTitle || '';
    const atcLabel = originalCard.dataset.listAtcLabel || 'Add to cart';
    const soldOutLabel = originalCard.dataset.listSoldOutLabel || 'Sold out';
    const seePriceLabel = originalCard.dataset.listSeePriceLabel || 'See your price';
    const retiredLabel = originalCard.dataset.listRetiredLabel?.trim() || 'Discontinued - Replaced by:';
    const replacedLabel = originalCard.dataset.listReplacedLabel?.trim() || 'Replaced by:';
    const temporaryLabel = originalCard.dataset.listTemporaryLabel?.trim() || 'Temporarily out of stock';
    const temporaryReplacedLabel = originalCard.dataset.listTemporaryReplacedLabel?.trim() || 'Temporarily out of stock - Replaced by:';
    const loginUrl = originalCard.dataset.listLoginUrl || '/account/login';
    const selectLabel = originalCard.dataset.listSelectLabel || 'Product variants';
    const quantityLabel = originalCard.dataset.listQuantityLabel || 'Quantity';
    const variants = getListViewVariants(originalCard);
    const unitsMap = originalCard._listUnitsMap || {};
    variants.forEach((variant) => {
        variant.listViewTitle = getListViewVariantLabel(variant, unitsMap);
    });
    const selectedVariant = getSelectedListVariant(originalCard, variants);
    const hasAvailableVariant = variants.some((variant) => variant.available);
    const selectedVariantAvailable = selectedVariant ? selectedVariant.available !== false : false;
    const priceText = selectedVariant ? formatListViewMoney(selectedVariant.price) : '';
    const artNr = escapeHtml(selectedVariant?.sku || originalCard.dataset.visibleVariantSku || '-');
    const escapedTitle = escapeHtml(productTitle);
    const escapedProductUrl = escapeHtml(productUrl);
    const escapedProductImage = escapeHtml(productImage);
    const escapedProductImageAlt = escapeHtml(productImageAlt);
    const escapedLoginUrl = escapeHtml(loginUrl);
    const escapedAtcLabel = escapeHtml(atcLabel);
    const escapedSoldOutLabel = escapeHtml(soldOutLabel);
    const escapedSeePriceLabel = escapeHtml(seePriceLabel);
    const escapedReplacementProductUrl = escapeHtml(replacementProductUrl);
    const escapedReplacementProductTitle = escapeHtml(replacementProductTitle);
    const escapedReplacementLabel = escapeHtml(
        replacementType === 'retired'
            ? retiredLabel
            : replacementType === 'temporary'
                ? temporaryReplacedLabel
                : replacedLabel
    );
    const escapedTemporaryLabel = escapeHtml(temporaryLabel);
    const buyIconHtml = renderListViewCartIcon();
    const soldOutIconHtml = renderListViewSoldOutIcon();
    const escapedQuantityLabel = escapeHtml(quantityLabel);
    const escapedSelectLabel = escapeHtml(selectLabel);

    if (!titleText && !productTitle) return '';

    if (!canShowCommerce || isReplacedProduct) {
        const replacementMarkup = isTemporarilyOutOfStock && !replacementProductUrl
            ? `
              <div class="list-item-replacement-message">
                <span>${escapedTemporaryLabel}</span>
              </div>
            `
            : isReplacedProduct && replacementProductUrl && replacementProductTitle
            ? `
              <div class="list-item-replacement-message">
                <span>${escapedReplacementLabel}</span>
                <a href="${escapedReplacementProductUrl}">${escapedReplacementProductTitle}</a>
              </div>
            `
            : `
              <div class="list-view-button-scheme list-view-button-scheme--login color-scheme-3 gradient">
                <a href="${escapedLoginUrl}?return_url=${encodeURIComponent(productUrl)}" class="button button--primary button--full-width list-view-buy-button list-view-buy-button--text">
                  ${escapedSeePriceLabel}
                </a>
              </div>
            `;
        return `
          <div class="list-view-content">
            <div class="list-view-row">
              <div class="list-item-media">
                ${productMiljoLogosHtml}
                <a href="${escapedProductUrl}" class="list-item-media-link">
                  ${
                    escapedProductImage
                      ? `<img src="${escapedProductImage}" alt="${escapedProductImageAlt}" loading="lazy" class="list-item-image">`
                      : `<div class="list-item-image product-image-fallback" role="img" aria-label="${escapedProductImageAlt}"></div>`
                  }
                </a>
              </div>
              <div class="list-item-details">
                <a class="list-item-title" href="${escapedProductUrl}">${escapedTitle}</a>
              <div class="list-item-art-nr">
                  <span class="list-item-art-nr-value" data-art-number>Art.Nr: ${artNr}</span>
                </div>
              </div>
            <div class="list-item-action${isReplacedProduct ? ' list-item-action--replacement' : ' list-item-action--login'}">
              ${replacementMarkup}
            </div>
            </div>
          </div>
        `;
    }

    const unitSelectorHTML = variants.length > 0
        ? renderListViewSelect(variants, selectedVariant, escapedSelectLabel)
        : '';

    return `
      <div class="list-view-content">
        <div class="list-view-row">
          <div class="list-item-media">
            ${productMiljoLogosHtml}
            <a href="${escapedProductUrl}" class="list-item-media-link">
              ${
                escapedProductImage
                  ? `<img src="${escapedProductImage}" alt="${escapedProductImageAlt}" loading="lazy" class="list-item-image">`
                  : `<div class="list-item-image product-image-fallback" role="img" aria-label="${escapedProductImageAlt}"></div>`
              }
            </a>
          </div>
          <div class="list-item-details">
            <a class="list-item-title" href="${escapedProductUrl}">${escapedTitle}</a>
            <div class="list-item-art-nr">
              <span class="list-item-art-nr-value" data-art-number>Art.Nr: ${artNr}</span>
            </div>
          </div>
          <div class="list-item-unit-selector">
            ${unitSelectorHTML}
          </div>
          <div class="list-item-quantity" aria-label="${escapedQuantityLabel}">
            <div class="quantity-input">
              <button type="button" class="quantity-btn quantity-decrease">-</button>
              <input type="number" class="quantity-field" value="1" min="1" max="99" inputmode="numeric">
              <button type="button" class="quantity-btn quantity-increase">+</button>
            </div>
          </div>
          <div class="list-item-price" data-list-price>${priceText}</div>
          <div class="list-item-action">
            <div class="list-view-button-scheme ${hasAvailableVariant && selectedVariantAvailable ? 'color-scheme-3 gradient' : 'list-view-button-scheme--disabled'}">
              <button
                type="button"
                class="list-add-to-cart list-view-buy-button list-view-buy-button--icon${hasAvailableVariant && selectedVariantAvailable ? '' : ' list-view-buy-button--disabled'}"
                data-form-id="${formId}"
                data-buy-label="${escapedAtcLabel}"
                data-sold-out-label="${escapedSoldOutLabel}"
                ${hasAvailableVariant && selectedVariantAvailable ? '' : 'disabled aria-disabled="true"'}
                aria-label="${hasAvailableVariant && selectedVariantAvailable ? escapedAtcLabel : escapedSoldOutLabel}"
              >
                ${hasAvailableVariant && selectedVariantAvailable ? buyIconHtml : soldOutIconHtml}
                <span class="visually-hidden">${hasAvailableVariant && selectedVariantAvailable ? escapedAtcLabel : escapedSoldOutLabel}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
}

function createListViewLayout(productElement, originalCard) {
    const markup = buildListViewMarkup(productElement, originalCard);
    if (!markup) return;

    originalCard.style.display = 'none';
    productElement.insertAdjacentHTML('beforeend', markup);
}

function convertToListView(listing) {
    const products = Array.from(listing.children).filter((child) => child.classList && child.classList.contains('grid__item'));

    products.forEach((product) => {
        if (!product.querySelector('.list-view-content')) {
            const originalCard = getCardElement(product);
            if (originalCard) {
                createListViewLayout(product, originalCard);
            }
        }
    });
}

function restoreGridView(listing) {
    const products = Array.from(listing.children).filter((child) => child.classList && child.classList.contains('grid__item'));

    products.forEach((product) => {
        const listViewContent = product.querySelector('.list-view-content');
        const originalCard = getCardElement(product);

        if (listViewContent) {
            listViewContent.remove();
        }

        if (originalCard) {
            originalCard.style.display = '';
        }
    });
}

function initViewToggle() {
    const toggleButtons = document.querySelectorAll('.view-toggle-btn');
    const productListingsSelector = '#product-grid, .product-grid, .productListing, .productListing2';
    const currentView = localStorage.getItem('collection-view-preference') || 'grid';

    function setView(view) {
        const productListings = document.querySelectorAll(productListingsSelector);

        toggleButtons.forEach((btn) => {
            btn.classList.toggle('active', btn.dataset.view === view);
        });

        productListings.forEach((listing) => {
            if (view === 'list') {
                listing.classList.add('list-view');
                convertToListView(listing);
            } else {
                listing.classList.remove('list-view');
                restoreGridView(listing);
            }
        });
    }

    setView(currentView);

    toggleButtons.forEach((button) => {
        button.addEventListener('click', function () {
            const view = this.dataset.view;
            setView(view);
            localStorage.setItem('collection-view-preference', view);
        });
    });

    return { setView };
}

function initListViewControls() {
    document.addEventListener('change', function (e) {
        if (!e.target.classList.contains('list-view-select')) return;

        const listContent = e.target.closest('.list-view-content');
        const productElement = e.target.closest('.grid__item');
        const originalCard = getCardElement(productElement);
        if (!listContent || !productElement || !originalCard) return;

        const actionButton = listContent.querySelector('.list-add-to-cart');

        const variants = getListViewVariants(originalCard);
        const selectedVariant = variants.find((variant) => String(variant.id) === String(e.target.value)) || null;
        const priceDisplay = listContent.querySelector('[data-list-price]');
        const artNumberDisplay = listContent.querySelector('[data-art-number]');
        const originalForm = originalCard.querySelector('form[action="/cart/add"]');
        const variantInput = originalForm?.querySelector('input[name="id"], select[name="id"]');

        if (variantInput) {
            variantInput.value = e.target.value;
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
            variantInput.dispatchEvent(new Event('input', { bubbles: true }));
        }

        if (selectedVariant && priceDisplay) {
            priceDisplay.textContent = formatListViewMoney(selectedVariant.price);
        }

        if (artNumberDisplay) {
            artNumberDisplay.textContent = `Art.Nr: ${selectedVariant?.sku || '-'}`;
        }

        if (actionButton) {
            const variantAvailable = selectedVariant ? selectedVariant.available !== false : false;
            const buttonLabel = variantAvailable ? actionButton.dataset.buyLabel : actionButton.dataset.soldOutLabel;
            actionButton.innerHTML = variantAvailable ? renderListViewCartIcon() : renderListViewSoldOutIcon();
            actionButton.insertAdjacentHTML('beforeend', `<span class="visually-hidden">${escapeHtml(buttonLabel || '')}</span>`);
            actionButton.disabled = !variantAvailable;
            actionButton.setAttribute('aria-disabled', variantAvailable ? 'false' : 'true');
            actionButton.setAttribute('aria-label', buttonLabel || '');
        }
    });

    document.addEventListener('click', function (e) {
        const decreaseButton = e.target.closest('.quantity-decrease');
        if (decreaseButton) {
            const quantityInput = decreaseButton.closest('.quantity-input')?.querySelector('.quantity-field');
            const currentVal = parseInt(quantityInput?.value || '1', 10);
            if (quantityInput && currentVal > 1) {
                quantityInput.value = currentVal - 1;
            }
        }

        const increaseButton = e.target.closest('.quantity-increase');
        if (increaseButton) {
            const quantityInput = increaseButton.closest('.quantity-input')?.querySelector('.quantity-field');
            const currentVal = parseInt(quantityInput?.value || '1', 10);
            if (quantityInput && currentVal < 99) {
                quantityInput.value = currentVal + 1;
            }
        }

        const buyButton = e.target.closest('.list-add-to-cart');
        if (!buyButton) return;

        const listContent = buyButton.closest('.list-view-content');
        const productElement = buyButton.closest('.grid__item');
        const originalCard = getCardElement(productElement);
        if (!listContent || !productElement || !originalCard) return;

        const actionButton = listContent.querySelector('.list-add-to-cart');
        if (actionButton && actionButton.disabled) return;

        const quantity = listContent.querySelector('.quantity-field')?.value || 1;
        const unitSelect = listContent.querySelector('.list-view-select');
        const formId = buyButton.dataset.formId;

        let originalForm = null;
        if (formId) {
            originalForm = document.getElementById(formId);
        }

        if (!originalForm) {
            originalForm = originalCard.querySelector('form[action="/cart/add"]');
        }

        if (!originalForm) return;

        const quantityInput = originalForm.querySelector('input[name="quantity"], .quantity-box');
        if (quantityInput) {
            quantityInput.value = quantity;
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (unitSelect && unitSelect.value) {
            const variantInput = originalForm.querySelector('input[name="id"], select[name="id"]');
            if (variantInput) {
                variantInput.value = unitSelect.value;
                variantInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        if (typeof originalForm.requestSubmit === 'function') {
            originalForm.requestSubmit();
        } else {
            const submitButton = originalForm.querySelector('button[type="submit"], button[name="add"], .add-to-cart-btn');
            if (submitButton) {
                submitButton.click();
            } else {
                originalForm.submit();
            }
        }
    });
}

window.reapplyViewPreference = function () {
    if (window.viewToggleInstance) {
        const currentView = localStorage.getItem('collection-view-preference') || 'grid';
        setTimeout(() => {
            window.viewToggleInstance.setView(currentView);
        }, 200);
    }
};

function initPaginationObserver() {
    const observer = new MutationObserver((mutations) => {
        let shouldReapplyView = false;

        mutations.forEach((mutation) => {
            if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) return;

            mutation.addedNodes.forEach((node) => {
                if (node.nodeType !== Node.ELEMENT_NODE) return;

                if (
                    node.id === 'product-grid' ||
                    (node.classList && node.classList.contains('product-grid')) ||
                    (node.classList && (node.classList.contains('productListing') || node.classList.contains('productListing2')))
                ) {
                    shouldReapplyView = true;
                    return;
                }

                if (node.querySelector && node.querySelector('.grid__item')) {
                    const currentView = localStorage.getItem('collection-view-preference') || 'grid';
                    if (currentView === 'list') {
                        const listings = node.matches?.('#product-grid, .product-grid, .productListing, .productListing2')
                            ? [node]
                            : Array.from(node.querySelectorAll('#product-grid, .product-grid, .productListing, .productListing2'));

                        listings.forEach((listing) => {
                            convertToListView(listing);
                            listing.classList.add('list-view');
                        });
                    }
                }
            });
        });

        if (shouldReapplyView && window.viewToggleInstance) {
            setTimeout(() => {
                const currentView = localStorage.getItem('collection-view-preference') || 'grid';
                window.viewToggleInstance.setView(currentView);
            }, 100);
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });
}

document.addEventListener('DOMContentLoaded', function () {
    window.viewToggleInstance = initViewToggle();
    initListViewControls();
    initPaginationObserver();
});
